import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Paper,
    Grid,
    List,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Typography,
    Toolbar,
    Button,
    makeStyles,
    FormControl,
    Select,
    MenuItem
} from "@material-ui/core";
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Settings as SettingsIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import EmailFolderList from "../../components/EmailFolderList";
import EmailListItem from "../../components/EmailListItem";
import EmailSignatureModal from "../../components/EmailSignatureModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "hidden", // Mudado de scroll para hidden
        ...theme.scrollbarStyles,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 140px)"
    },
    sidebar: {
        borderRight: `1px solid ${theme.palette.divider}`,
        overflowY: "auto",
        height: "100%"
    },
    emailList: {
        flex: 1, // Crucial para crescer e ocupar espaço restante
        overflowY: "auto",
        padding: 0,
        // Remover height: "100%" fixo pois o flex:1 já cuida disso no container flex
    },
    toolbar: {
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        minHeight: "56px" // Garantir altura mínima
    },
    searchField: {
        flex: 1
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: theme.spacing(4)
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(4),
        color: theme.palette.text.secondary
    }
}));

const Emails = () => {
    const classes = useStyles();
    const history = useHistory();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [smtpConfigs, setSmtpConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState("");
    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState("INBOX");
    const [emails, setEmails] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [unreadCounts, setUnreadCounts] = useState({});
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    const listRef = useRef(null);

    // Buscar configurações SMTP disponíveis
    useEffect(() => {
        const fetchSmtpConfigs = async () => {
            try {
                const { data } = await api.get("/smtp-configs");
                setSmtpConfigs(data);

                if (data.length > 0) {
                    const defaultConfig = data.find(c => c.isDefault) || data[0];
                    setSelectedConfig(defaultConfig.id);
                }
            } catch (err) {
                toastError(err);
            }
        };
        fetchSmtpConfigs();
    }, []);

    // Buscar pastas e resetar lista quando mudar configuração
    useEffect(() => {
        if (selectedConfig) {
            fetchFolders();
            setPage(1);
            setEmails([]); // Limpar lista ao mudar config
            fetchEmails(false, "INBOX"); // Forçar INBOX ao mudar conta
        }
    }, [selectedConfig]);

    const fetchFolders = async () => {
        if (!selectedConfig) return;

        try {
            const { data } = await api.get("/emails/folders", {
                params: { smtpConfigId: selectedConfig }
            });
            setFolders(data);
        } catch (err) {
            toastError(err);
        }
    };

    const fetchEmails = async (loadMore = false, folderOverwrite = null) => {
        if (!selectedConfig) return;

        const currentFolder = folderOverwrite || selectedFolder;

        if (loadMore) {
            if (loadingMore || !hasMore) return;
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        try {
            const currentPage = loadMore ? page + 1 : 1;
            console.log("[Emails] Buscando:", { folder: currentFolder, page: currentPage });

            const { data } = await api.get("/emails", {
                params: {
                    smtpConfigId: selectedConfig,
                    folder: currentFolder,
                    page: currentPage,
                    limit: 50,
                    query: searchQuery
                }
            });

            if (loadMore) {
                // Filtrar duplicatas por UID (garantir unicidade)
                setEmails(prev => {
                    const newEmails = data.emails || [];
                    const existingUids = new Set(prev.map(e => e.uid || e.id));
                    const uniqueNewEmails = newEmails.filter(e => !existingUids.has(e.uid || e.id));
                    return [...prev, ...uniqueNewEmails];
                });
                setPage(currentPage);
            } else {
                setEmails(data.emails || []);
                setPage(1);
            }

            setTotal(data.total || 0);

            // Lógica hasMore mais robusta: se temos menos carregados que o total real
            const totalFetched = loadMore ? emails.length + (data.emails?.length || 0) : (data.emails?.length || 0);
            const hasMoreData = totalFetched < (data.total || 0);

            console.log("[Emails] Status:", {
                folder: currentFolder,
                fetched: totalFetched,
                total: data.total,
                hasMore: hasMoreData,
                newCount: data.emails?.length
            });

            setHasMore(hasMoreData);

        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleScroll = (e) => {
        if (!hasMore || loading || loadingMore) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const threshold = 300;

        if (scrollHeight - scrollTop - clientHeight < threshold) {
            console.log("[Scroll] Ativado!", { scrollHeight, scrollTop, clientHeight });
            fetchEmails(true);
        }
    };

    const handleListItemClick = (email) => {
        history.push(`/emails/${selectedConfig}/${selectedFolder}/${email.uid}`);
    };

    const handleToggleStar = async (email, starred) => {
        try {
            await api.put(`/emails/${selectedConfig}/${selectedFolder}/${email.uid}/mark`, {
                action: starred ? "flag" : "unflag"
            });

            setEmails(prev => prev.map(e =>
                e.uid === email.uid
                    ? { ...e, flags: starred ? [...(e.flags || []), "\\Flagged"] : (e.flags || []).filter(f => f !== "\\Flagged") }
                    : e
            ));
        } catch (err) {
            toastError(err);
        }
    };

    const handleFolderChange = (folder) => {
        setSelectedFolder(folder);
        setPage(1);
        setEmails([]);
        fetchEmails(false, folder); // Chamar imediatamente com a nova pasta
    };

    const handleSearch = () => {
        setPage(1);
        fetchEmails(false);
    };

    const handleCompose = () => {
        history.push("/emails/compose", { smtpConfigId: selectedConfig });
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>E-mails</Title>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <FormControl variant="outlined" margin="dense" style={{ minWidth: 200, marginRight: 16 }}>
                        <Select
                            value={selectedConfig}
                            onChange={(e) => setSelectedConfig(e.target.value)}
                        >
                            {smtpConfigs.map((config) => (
                                <MenuItem key={config.id} value={config.id}>
                                    {config.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <IconButton
                        color="primary"
                        onClick={() => setSignatureModalOpen(true)}
                        disabled={!selectedConfig}
                        title="Configurar Assinatura"
                    >
                        <SettingsIcon />
                    </IconButton>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCompose}
                    >
                        Novo E-mail
                    </Button>
                </div>
            </MainHeader>
            <EmailSignatureModal
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                smtpConfigId={selectedConfig}
            />
            <Paper
                className={classes.mainPaper}
                variant="outlined"
            >
                <Grid container spacing={0} style={{ height: "100%" }}>
                    <Grid item xs={12} md={2} className={classes.sidebar}>
                        <EmailFolderList
                            selectedFolder={selectedFolder}
                            onFolderChange={handleFolderChange}
                            unreadCounts={unreadCounts}
                            folders={folders}
                        />
                    </Grid>

                    <Grid item xs={12} md={10} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                        <Toolbar className={classes.toolbar}>
                            <TextField
                                className={classes.searchField}
                                placeholder="Buscar e-mails..."
                                variant="outlined"
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <div style={{ margin: "0 10px" }}>
                                <Typography variant="caption" color="textSecondary">
                                    {emails.length} de {total}
                                </Typography>
                            </div>
                            <IconButton onClick={() => fetchEmails(false)} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Toolbar>

                        {loading && emails.length === 0 ? (
                            <div className={classes.loadingContainer}>
                                <CircularProgress />
                            </div>
                        ) : emails.length === 0 ? (
                            <div className={classes.emptyState}>
                                <Typography variant="h6">
                                    Nenhum e-mail encontrado
                                </Typography>
                                <Typography variant="body2">
                                    {searchQuery ? "Tente uma busca diferente" : "Esta pasta está vazia"}
                                </Typography>
                            </div>
                        ) : (
                            <List
                                className={classes.emailList}
                                ref={listRef}
                                onScroll={handleScroll}
                            >
                                {emails.map((email) => (
                                    <EmailListItem
                                        key={email.uid} // Garantir que UID é único (IMAP garante UID único por pasta)
                                        email={email}
                                        onClick={handleListItemClick}
                                        onToggleStar={handleToggleStar}
                                    />
                                ))}

                                {hasMore && !loadingMore && !loading && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                console.log("[Botão] Carregar mais clicado");
                                                fetchEmails(true);
                                            }}
                                        >
                                            Carregar mais e-mails
                                        </Button>
                                    </div>
                                )}

                                {loadingMore && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                                        <CircularProgress size={24} />
                                    </div>
                                )}
                            </List>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </MainContainer>
    );
};

export default Emails;
